import React, { useState } from 'react';
import { getInitials } from '../../utils/avatar';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  firstName?: string;
  lastName?: string;
  src?: string | null;
  alt?: string;
  size?: AvatarSize;
  className?: string;
  onClick?: () => void;
}

const sizeMap: Record<AvatarSize, { container: string; text: string }> = {
  sm: { container: 'w-8 h-8', text: 'text-xs' },
  md: { container: 'w-12 h-12', text: 'text-base' },
  lg: { container: 'w-24 h-24', text: 'text-3xl' },
  xl: { container: 'w-32 h-32', text: 'text-4xl' },
};

const Avatar: React.FC<AvatarProps> = ({
  firstName,
  lastName,
  src,
  alt,
  size = 'md',
  className = '',
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const { container, text } = sizeMap[size];
  const initials = getInitials(firstName, lastName);
  const displayAlt = alt || `${firstName || ''} ${lastName || ''}`.trim() || 'User avatar';

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={displayAlt}
        className={`${container} rounded-full object-cover border-2 border-border ${className} ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`${container} bg-brand rounded-full flex items-center justify-center text-white font-bold ${text} border-2 border-border ${className} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={displayAlt}
    >
      {initials}
    </div>
  );
};

export { Avatar };
export type { AvatarProps, AvatarSize };
